// The candidate flow behind a one-time link: /t/:token.
//
// State machine: loading → landing | questionnaire | results | completed |
// invalid (| error on network failure). Which branch we land on comes from
// GET /api/test/:token on mount — unknown/voided links 404 (invalid),
// spent links report completed, live ones return the 120 questions.
//
// Adapted from careers.co.jp routes/candidate/BigFive.tsx (the questionnaire
// pages, Likert buttons, results layout), with three additions that repo
// doesn't have:
//   1. localStorage draft keyed by token — a refresh mid-test keeps all
//      answers, the current page, the language, and the timer.
//   2. Active-time tracking — a 1s interval that only counts while the tab
//      is visible, split per page. Sent with the submission; the candidate
//      only ever sees a neutral wall-clock timer (which never pauses, so the
//      tracking stays invisible).
//   3. Copy deterrence — select-none + suppressed copy events, with
//      copy/tab-hidden/focus-loss beacons to the events endpoint
//      (questionnaire only).

import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { Card, CardBody, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { BigFiveChart } from "@/components/bigfive-chart";
import { Attribution } from "@/components/attribution";
import { FacetReport } from "@/components/facet-report";
import { Page } from "@/components/page";
import { ThemeToggle } from "@/components/theme-toggle";
import { cn } from "@/lib/utils";
import {
  api,
  ApiError,
  formatMmSs,
  type Lang,
  type PublicQuestion,
  type SubmitBody,
  type TestResults,
  type TestStatusResponse,
} from "@/lib/api";

const PER_PAGE = 10;

// Same five answer labels as the parent's Likert row — keep EN+JA verbatim.
const LIKERT = [
  { value: 1, labelEn: "Strongly disagree", labelJa: "全くそう思わない" },
  { value: 2, labelEn: "Disagree", labelJa: "そう思わない" },
  { value: 3, labelEn: "Neutral", labelJa: "どちらでもない" },
  { value: 4, labelEn: "Agree", labelJa: "そう思う" },
  { value: 5, labelEn: "Strongly agree", labelJa: "強くそう思う" },
];

type Phase =
  | "loading"
  | "landing"
  | "questionnaire"
  | "results"
  | "completed"
  | "invalid"
  | "error";

/** What survives a refresh, stored under a token-derived key. */
interface Draft {
  /** Answered values keyed by String(question order). */
  answers: Record<string, number>;
  page: number;
  activeSeconds: number;
  perPage: number[];
  lang: Lang;
  /** Epoch ms when the questionnaire first started. Drives the visible
   *  wall-clock timer; set once and kept across refreshes. */
  startedAt: number;
}

function draftKeyFor(token: string): string {
  return `bigfive-draft:${token}`;
}

/** Parse + sanity-check a stored draft. Anything malformed (old shape,
 *  corrupted JSON, out-of-range values) is treated as no draft at all. */
function readDraft(key: string, questionCount: number, totalPages: number): Draft | null {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(key);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const d = JSON.parse(raw) as Partial<Draft>;
    if (typeof d !== "object" || d === null) return null;
    const answers: Record<string, number> = {};
    if (typeof d.answers === "object" && d.answers !== null) {
      for (const [k, v] of Object.entries(d.answers)) {
        const order = Number(k);
        if (
          Number.isInteger(order) &&
          order >= 1 &&
          order <= questionCount &&
          typeof v === "number" &&
          Number.isInteger(v) &&
          v >= 1 &&
          v <= 5
        ) {
          answers[k] = v;
        }
      }
    }
    const page =
      typeof d.page === "number" && Number.isInteger(d.page) && d.page >= 0 && d.page < totalPages
        ? d.page
        : 0;
    const activeSeconds =
      typeof d.activeSeconds === "number" && Number.isFinite(d.activeSeconds) && d.activeSeconds >= 0
        ? Math.floor(d.activeSeconds)
        : 0;
    const perPage = Array.from({ length: totalPages }, (_, i) => {
      const v = Array.isArray(d.perPage) ? d.perPage[i] : 0;
      return typeof v === "number" && Number.isFinite(v) && v >= 0 ? Math.floor(v) : 0;
    });
    const lang: Lang = d.lang === "ja" ? "ja" : "en";
    // Older drafts predate startedAt — treat the restore moment as the start
    // rather than throwing the whole draft away.
    const startedAt =
      typeof d.startedAt === "number" && Number.isFinite(d.startedAt) && d.startedAt > 0
        ? d.startedAt
        : Date.now();
    return { answers, page, activeSeconds, perPage, lang, startedAt };
  } catch {
    return null;
  }
}

function LangToggle({ lang, onChange }: { lang: Lang; onChange: (l: Lang) => void }) {
  return (
    <div
      role="group"
      aria-label="Language"
      className="inline-flex items-center rounded-full border border-paper-line bg-paper-raised p-0.5"
    >
      {(["en", "ja"] as const).map((l) => (
        <button
          key={l}
          type="button"
          aria-pressed={lang === l}
          onClick={() => onChange(l)}
          className={cn(
            "rounded-full px-3 py-1 text-[11px] font-medium transition-colors",
            lang === l ? "bg-accent text-paper" : "text-ink-muted hover:text-ink",
          )}
        >
          {l === "en" ? "EN" : "日本語"}
        </button>
      ))}
    </div>
  );
}

/** Overline branding on the left, theme + language toggles on the right —
 *  the one fixed header row every candidate screen shares. */
function TopBar({ lang, onLang }: { lang: Lang; onLang: (l: Lang) => void }) {
  return (
    <div className="mb-6 flex items-center justify-between">
      <p className="inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.18em] text-ink-muted">
        <Sparkles className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
        AGP · Big Five
      </p>
      {/* Theme before language, matching the parent TopBar's order. */}
      <div className="flex items-center gap-1.5">
        <ThemeToggle />
        <LangToggle lang={lang} onChange={onLang} />
      </div>
    </div>
  );
}

/** Calm terminal screens (invalid link / already completed / load error). */
function NoticeScreen({
  lang,
  onLang,
  title,
  body,
}: {
  lang: Lang;
  onLang: (l: Lang) => void;
  title: string;
  body: string;
}) {
  return (
    <Page>
      <TopBar lang={lang} onLang={onLang} />
      <Card>
        <CardBody className="space-y-2 py-8 text-center">
          <h1 className="font-serif text-2xl">{title}</h1>
          <p className="text-sm text-ink-muted">{body}</p>
        </CardBody>
      </Card>
    </Page>
  );
}

export function TestPage() {
  const { token = "" } = useParams();
  const storageKey = draftKeyFor(token);

  const [phase, setPhase] = useState<Phase>("loading");
  const [questions, setQuestions] = useState<PublicQuestion[]>([]);
  const [lang, setLang] = useState<Lang>("en");
  const [page, setPage] = useState(0);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [results, setResults] = useState<TestResults | null>(null);
  const [restored, setRestored] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitFailed, setSubmitFailed] = useState(false);

  // Ref mirrors so the 1s interval, the beacon handlers, and submit always
  // read the latest values without re-subscribing every render.
  const activeSecondsRef = useRef(0);
  const perPageRef = useRef<number[]>([]);
  // Epoch ms when the questionnaire first started (0 = not started yet).
  // The visible timer is wall-clock from this anchor; activeSecondsRef stays
  // the hidden-pausing accumulator that gets submitted.
  const startedAtRef = useRef(0);
  const pageRef = useRef(page);
  const answersRef = useRef(answers);
  const langRef = useRef(lang);
  const phaseRef = useRef(phase);
  pageRef.current = page;
  answersRef.current = answers;
  langRef.current = lang;
  phaseRef.current = phase;

  const totalPages = Math.max(1, Math.ceil(questions.length / PER_PAGE));
  const answeredCount = Object.keys(answers).length;

  const saveDraft = () => {
    try {
      const draft: Draft = {
        answers: answersRef.current,
        page: pageRef.current,
        activeSeconds: activeSecondsRef.current,
        perPage: perPageRef.current,
        lang: langRef.current,
        startedAt: startedAtRef.current,
      };
      localStorage.setItem(storageKey, JSON.stringify(draft));
    } catch {
      // Storage blocked/full — the test still works, a refresh just won't restore.
    }
  };

  // Copy/tab-away/focus-loss beacons — questionnaire only, and always via a
  // Blob typed application/json: a bare string arrives as text/plain and gets
  // dropped.
  const beacon = (type: "copy" | "hidden" | "blur") => {
    if (phaseRef.current !== "questionnaire") return;
    const blob = new Blob([JSON.stringify({ type })], { type: "application/json" });
    navigator.sendBeacon(`/api/test/${encodeURIComponent(token)}/events`, blob);
  };

  // Mount: ask the API which branch this token is on, and restore any draft.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api<TestStatusResponse>(`/api/test/${encodeURIComponent(token)}`);
        if (cancelled) return;
        if (data.status === "completed") {
          setPhase("completed");
          return;
        }
        const qs = data.questions;
        const pages = Math.max(1, Math.ceil(qs.length / PER_PAGE));
        perPageRef.current = Array.from({ length: pages }, () => 0);
        setQuestions(qs);
        const draft = readDraft(storageKey, qs.length, pages);
        if (draft) {
          setAnswers(draft.answers);
          setPage(draft.page);
          setLang(draft.lang);
          activeSecondsRef.current = draft.activeSeconds;
          perPageRef.current = draft.perPage;
          startedAtRef.current = draft.startedAt;
          setElapsed(Math.max(0, Math.floor((Date.now() - draft.startedAt) / 1000)));
          if (Object.keys(draft.answers).length > 0) setRestored(true);
        }
        setPhase("landing");
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) setPhase("invalid");
        else setPhase("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, storageKey]);

  // The clocks. One interval for the whole questionnaire, driving two
  // different timers:
  //   - The visible timer is wall-clock from startedAt, recomputed every
  //     tick. It must NOT pause while the tab is hidden — a frozen clock
  //     would leak that we track active time. Recomputing from the anchor
  //     also means it catches up naturally after the tab was hidden.
  //   - activeSeconds/perPage only count while the tab is visible; they are
  //     the hidden-pausing signal that gets submitted, never shown.
  // Ticks also persist the draft, so both timers survive a refresh.
  useEffect(() => {
    if (phase !== "questionnaire") return;
    // First start on this device (no draft restored): anchor the wall clock.
    if (startedAtRef.current === 0) startedAtRef.current = Date.now();
    const tick = window.setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000));
      if (document.visibilityState !== "visible") return;
      activeSecondsRef.current += 1;
      const p = pageRef.current;
      perPageRef.current[p] = (perPageRef.current[p] ?? 0) + 1;
      saveDraft();
    }, 1000);
    const onVisibility = () => {
      if (document.visibilityState === "hidden") beacon("hidden");
    };
    // Focus lost to another window/monitor while this page stays visible.
    // A tab switch already fires the `hidden` beacon, so only report blur
    // while the page is still visible — otherwise one switch counts twice.
    const onBlur = () => {
      if (document.visibilityState === "visible") beacon("blur");
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("blur", onBlur);
    return () => {
      window.clearInterval(tick);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("blur", onBlur);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // Persist on every answer/page/language change (ticks cover the timer).
  useEffect(() => {
    if (phase !== "questionnaire") return;
    saveDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, answers, page, lang]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [page]);

  const submit = async () => {
    setSubmitting(true);
    setSubmitFailed(false);
    const body: SubmitBody = {
      answers: Object.entries(answersRef.current)
        .map(([order, value]) => ({ order: Number(order), value }))
        .sort((a, b) => a.order - b.order),
      lang: langRef.current,
      timings: {
        activeSeconds: activeSecondsRef.current,
        perPage: perPageRef.current.map((s) => Math.round(s)),
      },
    };
    try {
      const out = await api<{ results: TestResults }>(
        `/api/test/${encodeURIComponent(token)}/submit`,
        { method: "POST", body },
      );
      try {
        localStorage.removeItem(storageKey);
      } catch {
        // Ignore — worst case a spent draft lingers on this device.
      }
      setResults(out.results);
      setPhase("results");
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        // The link was spent elsewhere; a stale draft would only confuse.
        try {
          localStorage.removeItem(storageKey);
        } catch {
          // Same as above.
        }
        setPhase("completed");
      } else {
        setSubmitFailed(true);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ----- Screens -----------------------------------------------------------

  if (phase === "loading") {
    return (
      <Page>
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-32 w-full rounded-md" />
          ))}
        </div>
      </Page>
    );
  }

  if (phase === "invalid") {
    return (
      <NoticeScreen
        lang={lang}
        onLang={setLang}
        title={lang === "ja" ? "このリンクは無効です。" : "This link is not valid."}
        body={
          lang === "ja"
            ? "お心当たりのある場合は、招待を送付した担当者にお問い合わせください。"
            : "If you believe this is a mistake, please contact the person who invited you."
        }
      />
    );
  }

  if (phase === "completed") {
    return (
      <NoticeScreen
        lang={lang}
        onLang={setLang}
        title={
          lang === "ja"
            ? "このテストは既に完了しています。"
            : "You have already completed this test."
        }
        body={
          lang === "ja"
            ? "回答は記録されています。このリンクは1回のみ有効です。"
            : "Your responses have been recorded. This link works only once."
        }
      />
    );
  }

  if (phase === "error") {
    return (
      <NoticeScreen
        lang={lang}
        onLang={setLang}
        title={lang === "ja" ? "読み込みに失敗しました。" : "Could not load the test."}
        body={
          lang === "ja"
            ? "ページを再読み込みしてもう一度お試しください。"
            : "Please reload the page and try again."
        }
      />
    );
  }

  if (phase === "results" && results) {
    return (
      <Page>
        <TopBar lang={lang} onLang={setLang} />
        <header className="mb-6">
          <h1 className="font-serif text-3xl">
            {lang === "ja" ? "ありがとうございました。" : "Thank you."}
          </h1>
          <p className="text-ink-muted mt-2">
            {lang === "ja"
              ? "回答を記録しました。結果はメールでもお送りします。"
              : "Your responses have been recorded. The results are also on their way to your email."}
          </p>
        </header>
        <BigFiveChart scores={results.percentiles} lang={lang} />
        <Card className="mt-5">
          <CardBody className="space-y-2">
            {lang === "ja" ? (
              <p className="text-[11px] text-ink-muted">
                サマリーは現在英語のみでのご提供です。
              </p>
            ) : null}
            <p className="text-sm text-ink-soft leading-relaxed">{results.summary}</p>
          </CardBody>
        </Card>
        <FacetReport facets={results.facets} lang={lang} />
        <Attribution lang={lang} />
      </Page>
    );
  }

  if (phase === "landing") {
    const facts: Array<{ en: string; ja: string }> = [
      { en: "120 short statements", ja: "全120問の短い設問" },
      { en: "About 10–15 minutes", ja: "所要時間 約10〜15分" },
      {
        en: "Progress is saved on this device — reloading is safe",
        ja: "途中経過はこの端末に保存されます（再読み込みしても大丈夫です）",
      },
      {
        en: "This link works once; answers are final after you submit",
        ja: "このリンクは1回のみ有効です。送信後の変更はできません",
      },
    ];
    return (
      <Page>
        <TopBar lang={lang} onLang={setLang} />
        <Card>
          <CardBody className="space-y-4">
            <h1 className="font-serif text-3xl">
              {lang === "ja" ? "ビッグファイブ性格診断" : "Big Five personality test"}
            </h1>
            <p className="text-ink-muted leading-relaxed">
              {lang === "ja"
                ? "性格特性を5つの領域で測る IPIP-NEO-120 に基づく診断です。次の各文に対して、自分にどの程度当てはまるかを5段階で選んでください。正解・不正解はありません。"
                : "A personality assessment based on the IPIP-NEO-120, measuring five broad trait domains. For each statement, choose how well it describes you on a five-point scale. There are no right or wrong answers."}
            </p>
            <ul className="space-y-1.5 text-sm text-ink-soft">
              {facts.map((f) => (
                <li key={f.en} className="flex items-start gap-2">
                  <span aria-hidden className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                  {lang === "ja" ? f.ja : f.en}
                </li>
              ))}
            </ul>
          </CardBody>
          <CardFooter>
            <Button size="lg" onClick={() => setPhase("questionnaire")}>
              {restored
                ? lang === "ja"
                  ? "続きから再開"
                  : "Continue"
                : lang === "ja"
                  ? "開始する"
                  : "Start"}
            </Button>
            {restored ? (
              <span className="text-xs text-ink-muted">
                {lang === "ja" ? "回答を復元しました" : "Answers restored"}
              </span>
            ) : null}
          </CardFooter>
        </Card>
        <Attribution lang={lang} />
      </Page>
    );
  }

  // ----- Questionnaire -----------------------------------------------------

  const slice = questions.slice(page * PER_PAGE, (page + 1) * PER_PAGE);
  const allOnPageAnswered = slice.every((q) => answers[String(q.order)] !== undefined);
  const progressPct = questions.length > 0 ? (answeredCount / questions.length) * 100 : 0;

  return (
    <Page>
      <TopBar lang={lang} onLang={setLang} />
      {/* select-none + the suppressed copy handler are the deterrence layer;
          the beacon makes the attempt visible on the dashboard. */}
      <div
        className="select-none"
        onCopy={(e) => {
          e.preventDefault();
          beacon("copy");
        }}
      >
        {restored ? (
          <p className="mb-4 rounded-md border border-paper-line bg-paper-raised px-3 py-2 text-[13px] text-ink-muted">
            {lang === "ja" ? "回答を復元しました。" : "Your earlier answers were restored."}
          </p>
        ) : null}

        <header className="mb-6">
          <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-ink-muted">
            {lang === "ja" ? `ページ ${page + 1} / ${totalPages}` : `Page ${page + 1} of ${totalPages}`}
          </p>
          <h1 className="font-serif text-3xl mt-2">
            {lang === "ja" ? "ビッグファイブ性格診断" : "Big Five personality test"}
          </h1>
          <p className="text-ink-muted leading-relaxed mt-2">
            {lang === "ja"
              ? "次の各文に対して、自分にどの程度当てはまるかを選んでください。"
              : "For each statement, select how well it describes you."}
          </p>
          <div className="mt-4 flex items-center gap-3">
            <div className="h-1.5 flex-1 bg-paper-sunken rounded-full overflow-hidden">
              <div
                className="h-full bg-accent transition-all rounded-full"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            {/* Neutral elapsed timer — informational only, enforces nothing. */}
            <span className="shrink-0 text-xs tabular-nums text-ink-muted">
              {Math.round(progressPct)}% · {formatMmSs(elapsed)}
            </span>
          </div>
        </header>

        <div className="space-y-4">
          {slice.map((q) => (
            <Card key={q.order}>
              <CardBody className="space-y-3">
                <p className="font-serif text-lg leading-snug">
                  {lang === "ja" ? q.ja : q.en}
                </p>
                <div className="grid grid-cols-5 gap-1.5">
                  {LIKERT.map((opt) => {
                    const selected = answers[String(q.order)] === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() =>
                          setAnswers({ ...answers, [String(q.order)]: opt.value })
                        }
                        className={`px-2 py-2 text-[11px] sm:text-xs rounded-md border transition-colors leading-tight ${
                          selected
                            ? "border-accent bg-accent text-paper"
                            : "border-paper-line text-ink-muted hover:text-ink hover:bg-paper-sunken"
                        }`}
                      >
                        {lang === "ja" ? opt.labelJa : opt.labelEn}
                      </button>
                    );
                  })}
                </div>
              </CardBody>
            </Card>
          ))}
        </div>

        <Card className="mt-5">
          <CardFooter className="flex items-center justify-between">
            <Button
              variant="secondary"
              disabled={page === 0}
              onClick={() => setPage(page - 1)}
            >
              {lang === "ja" ? "前へ" : "Previous"}
            </Button>
            <span className="text-xs text-ink-muted tabular-nums">
              {answeredCount} / {questions.length}
            </span>
            {page + 1 < totalPages ? (
              <Button disabled={!allOnPageAnswered} onClick={() => setPage(page + 1)}>
                {lang === "ja" ? "次へ" : "Next"}
              </Button>
            ) : (
              <Button
                disabled={answeredCount !== questions.length || submitting}
                onClick={() => void submit()}
              >
                {lang === "ja" ? "送信" : "Submit"}
              </Button>
            )}
          </CardFooter>
          {submitFailed ? (
            <p className="px-5 pb-3 text-xs text-danger-on">
              {lang === "ja"
                ? "送信に失敗しました。通信環境を確認して、もう一度お試しください。"
                : "Submitting failed. Check your connection and try again."}
            </p>
          ) : null}
        </Card>
      </div>
      <Attribution lang={lang} />
    </Page>
  );
}
